import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatNumber, formatPropertyStyle } from '../utils/formatters';
import { useComparison } from '../context/ComparisonContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useAuthPopup } from '../hooks/useAuthPopup';
import { Calculator } from 'lucide-react';
import ValuationDisplay from './ValuationDisplay';
import './PropertyDetailsContent.css';

const PropertyDetailsContent = ({ property }) => {
    const { analysis } = property;

    // Helper for profit color
    const getProfitColor = (profit) => {
        if (profit === null || profit === undefined) return 'var(--gray-700)';
        return profit >= 0 ? '#059669' : '#dc2626';
    };

    // Rent-only property: has rent estimate but no ARV
    const isRentOnly = !property.arv && property.estimated_rent;

    const navigate = useNavigate();
    const { addToCompare, removeFromCompare, isInCompare, compareCount } = useComparison();
    const { showToast } = useToast();
    const { user } = useAuth();
    const { openAuthPopup } = useAuthPopup();
    const inCompare = isInCompare(property.property_id);

    const handleCompareToggle = () => {
        if (inCompare) {
            removeFromCompare(property.property_id);
        } else {
            if (compareCount >= 5) {
                showToast('Maximum 5 properties for comparison', 'warning');
                return;
            }
            addToCompare(property);
        }
    };

    const handleScenarioClick = () => {
        if (!user) {
            openAuthPopup(() => navigate(`/scenarios/${property.property_id}`));
            return;
        }
        navigate(`/scenarios/${property.property_id}`);
    };

    return (
        <div className="property-details-content">
            {/* Header Section */}
            <div className="pd-header">
                <div>
                    <h2 className="pd-address">{property.address}, {property.city} {property.state}</h2>
                    <div className="pd-subheader">
                        <span className="pd-style">{formatPropertyStyle(property.style)}</span>
                        {property.list_date && (() => {
                            const days = Math.floor((Date.now() - new Date(property.list_date).getTime()) / 86400000);
                            return days > 0 ? (
                                <span className="pd-days-badge">{days} days on market</span>
                            ) : null;
                        })()}
                    </div>
                </div>
                <div className="pd-actions">
                    <button
                        className={`add-compare-btn ${inCompare ? 'in-compare' : ''}`}
                        onClick={handleCompareToggle}
                    >
                        {inCompare ? '✓ In Compare' : '+ Add to Compare'}
                    </button>
                    <button className="pd-btn-scenario" onClick={handleScenarioClick}>
                        <Calculator size={14} />
                        Run Analysis
                    </button>
                </div>
            </div>

            {/* Key Metrics Row */}
            <div className="pd-metrics-row">
                <div className="pd-metric">
                    <span className="pd-metric-value">{formatCurrency(property.list_price)}</span>
                    <span className="pd-metric-label">List Price</span>
                </div>
                <div className="pd-metric">
                    <span className="pd-metric-value">
                        {property.arv > 0 ? formatCurrency(property.arv) : 'N/A'}
                    </span>
                    <span className="pd-metric-label">Est. ARV</span>
                </div>
                {isRentOnly ? (
                    <div className="pd-metric highlight">
                        <span className="pd-metric-value positive">
                            {formatCurrency(property.estimated_rent)}/mo
                        </span>
                        <span className="pd-metric-label">Est. Rent</span>
                    </div>
                ) : (
                    <div className="pd-metric highlight">
                        <span className={`pd-metric-value ${property.roi_percentage >= 0 ? 'positive' : 'negative'}`}>
                            {property.roi_percentage != null ? `${property.roi_percentage.toFixed(1)}%` : 'N/A'}
                        </span>
                        <span className="pd-metric-label">Est. ROI</span>
                    </div>
                )}
                <div className="pd-metric">
                    <span className="pd-metric-value" style={{ color: getProfitColor(property.expected_profit) }}>
                        {property.expected_profit != null ? formatCurrency(property.expected_profit) : 'N/A'}
                    </span>
                    <span className="pd-metric-label">Exp. Profit</span>
                </div>
                {/* Risk Score removed as requested */}
            </div>

            {/* Property Details & Description - Combined Card */}
            <div className="pd-section">
                <h3 className="pd-section-title">Property Details</h3>
                <div className="pd-specs-grid">
                    <div className="pd-spec-item">
                        <span className="pd-spec-icon">🛏️</span>
                        <span className="pd-spec-value">{property.beds || '—'}</span>
                        <span className="pd-spec-label">Beds</span>
                    </div>
                    <div className="pd-spec-item">
                        <span className="pd-spec-icon">🛁</span>
                        <span className="pd-spec-value">{property.baths || '—'}</span>
                        <span className="pd-spec-label">Baths</span>
                    </div>
                    <div className="pd-spec-item">
                        <span className="pd-spec-icon">📐</span>
                        <span className="pd-spec-value">{property.sqft ? formatNumber(property.sqft) : '—'}</span>
                        <span className="pd-spec-label">Sq Ft</span>
                    </div>
                    <div className="pd-spec-item">
                        <span className="pd-spec-icon">🏗️</span>
                        <span className="pd-spec-value">{property.year_built || '—'}</span>
                        <span className="pd-spec-label">Year Built</span>
                    </div>
                </div>

                {/* Description within same card */}
                {property.description && (
                    <>
                        <h4 className="pd-subsection-title">📝 Description</h4>
                        <div className="pd-description-box">
                            <p>{property.description}</p>
                        </div>
                    </>
                )}
            </div>

            {/* Financial Analysis */}
            <div className="pd-section">
                <h3 className="pd-section-title">
                    {isRentOnly ? 'Rental Financial Summary' : 'Financial Analysis'}
                </h3>
                <div className="pd-financials-grid">
                    {!isRentOnly && (
                        <>
                            <div className="pd-fin-row">
                                <span className="pd-fin-label">
                                    Est. Resale Price (ARV)
                                    {property.arv_source === 'llm' && <span className="pd-badge ai">AI Estimate</span>}
                                    {property.arv_source === 'xgboost' && <span className="pd-badge ml">ML Estimate</span>}
                                </span>
                                <span className="pd-fin-value">
                                    {property.arv > 0 ? formatCurrency(property.arv) : 'N/A'}
                                </span>
                            </div>
                            <div className="pd-fin-row">
                                <span className="pd-fin-label">Est. Renovation Cost</span>
                                <span className="pd-fin-value negative">
                                    {property.renovation_cost > 0 ? `- ${formatCurrency(property.renovation_cost)}` : 'N/A'}
                                </span>
                            </div>
                            <div className="pd-fin-row">
                                <span className="pd-fin-label">Carrying & Selling Costs</span>
                                <span className="pd-fin-value negative">
                                    {property.carrying_and_selling_costs > 0 ? `- ${formatCurrency(property.carrying_and_selling_costs)}` : 'N/A'}
                                </span>
                            </div>
                            <div className="pd-fin-row total">
                                <span className="pd-fin-label">Expected Profit</span>
                                <span className="pd-fin-value" style={{ color: getProfitColor(property.expected_profit) }}>
                                    {property.expected_profit != null ? formatCurrency(property.expected_profit) : 'N/A'}
                                </span>
                            </div>
                        </>
                    )}
                    {isRentOnly && (
                        <div className="pd-fin-row" style={{ padding: '12px 0', color: '#6b7280', fontSize: '14px' }}>
                            <span>ARV estimation was not performed for this property. It qualifies as a rental investment based on the 1% rule.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Rental Analysis */}
            <div className="pd-section">
                <h3 className="pd-section-title">Rental Analysis</h3>
                <div className="pd-financials-grid">
                    <div className="pd-fin-row">
                        <span className="pd-fin-label">
                            Est. Monthly Rent
                            {property.rent_confidence && (
                                <span className={`pd-badge ${property.rent_confidence}`}>
                                    {property.rent_confidence.toUpperCase()} Confidence
                                </span>
                            )}
                        </span>
                        <span className="pd-fin-value">
                            {property.estimated_rent ? formatCurrency(property.estimated_rent) : 'N/A'}
                        </span>
                    </div>

                    {property.rent_explanation && (
                        <div className="pd-fin-row full-width" style={{ marginTop: '0.5rem', display: 'block' }}>
                            <span className="pd-fin-label" style={{ display: 'block', marginBottom: '4px' }}>Analysis</span>
                            <span className="pd-fin-value text-small" style={{ display: 'block', fontWeight: 'normal', color: 'var(--gray-600)' }}>
                                {property.rent_explanation}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* AI Analysis */}
            <div className="pd-section">
                {/* Use structured ValuationDisplay if available, else fallback to text */}
                <ValuationDisplay
                    arv={property.arv}
                    valuationDetails={property.valuation_details}
                    fallbackExplanation={property.explanation}
                    subjectDetails={property}
                />
            </div>

            {/* Deal Profile, Opportunities & Risks - Combined Card */}
            {analysis && (
                <div className="pd-section">
                    <h3 className="pd-section-title">📊 Deal Profile & Analysis</h3>

                    {/* Investor Tags */}
                    {analysis.investor_tags && analysis.investor_tags.length > 0 && (
                        <>
                            <h4 className="pd-subsection-title">Deal Profile</h4>
                            <div className="pd-deal-tags">
                                {analysis.investor_tags.map((tag, idx) => (
                                    <span key={idx} className="pd-deal-tag">{tag}</span>
                                ))}
                            </div>
                        </>
                    )}

                    {/* Opportunities & Risks */}
                    <div className="pd-analysis-sections">
                        {/* Opportunities Section */}
                        {analysis.opportunities?.length > 0 && (
                            <div className="pd-analysis-group">
                                <h4 className="pd-analysis-group-title">
                                    <span className="pd-group-icon">✅</span>
                                    Opportunities
                                </h4>
                                <div className="pd-analysis-box positive">
                                    {analysis.opportunities.map((item, i) => (
                                        <p key={i} className="pd-analysis-item">{item}</p>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Risks Section */}
                        <div className="pd-analysis-group">
                            <h4 className="pd-analysis-group-title">
                                <span className="pd-group-icon">
                                    {analysis.risks?.length > 0 ? '⚠️' : '✅'}
                                </span>
                                {analysis.risks?.length > 0 ? 'Risks' : 'No Significant Risks'}
                            </h4>
                            {analysis.risks?.length > 0 ? (
                                <div className="pd-analysis-box negative">
                                    {analysis.risks.map((item, i) => (
                                        <p key={i} className="pd-analysis-item">{item}</p>
                                    ))}
                                </div>
                            ) : (
                                <div className="pd-analysis-box positive">
                                    <p className="pd-analysis-item">
                                        This property has no significant risks identified based on available data.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PropertyDetailsContent;
